// CustomScenarioBuilder — Phase 3: Custom scenario weight/constraint editor
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Slider,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { useGenerateCustomScenario } from '../../../hooks/useBomIntelligence';
import { colors } from '../../../theme/analyticsColors';

interface CustomScenarioBuilderProps {
  bomId: string;
  open: boolean;
  onClose: () => void;
}

const CustomScenarioBuilder: React.FC<CustomScenarioBuilderProps> = ({
  bomId,
  open,
  onClose,
}) => {
  const [name, setName] = useState('Custom Scenario');
  const [weights, setWeights] = useState({
    cost: 25,
    leadTime: 25,
    risk: 25,
    diversification: 25,
  });
  const [maxCost, setMaxCost] = useState('');
  const [maxLeadTime, setMaxLeadTime] = useState('');
  const [minSupplierCount, setMinSupplierCount] = useState('');

  const generateMutation = useGenerateCustomScenario();

  const totalWeight = weights.cost + weights.leadTime + weights.risk + weights.diversification;
  const isValid = name.trim() && totalWeight === 100;

  const handleWeightChange = (field: keyof typeof weights, value: number) => {
    setWeights((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerate = () => {
    if (!isValid) return;

    const constraints: Record<string, any> = {};
    if (maxCost) constraints.maxCost = parseFloat(maxCost);
    if (maxLeadTime) constraints.maxLeadTimeDays = parseInt(maxLeadTime, 10);
    if (minSupplierCount) constraints.minSupplierCount = parseInt(minSupplierCount, 10);

    generateMutation.mutate(
      {
        bomId,
        dto: {
          name: name.trim(),
          weights,
          constraints: Object.keys(constraints).length > 0 ? constraints : undefined,
        },
      },
      {
        onSuccess: () => {
          onClose();
          setName('Custom Scenario');
          setWeights({ cost: 25, leadTime: 25, risk: 25, diversification: 25 });
          setMaxCost('');
          setMaxLeadTime('');
          setMinSupplierCount('');
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
        Create Custom Scenario
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Scenario Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          sx={{ mb: 3, mt: 1 }}
        />

        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1.5 }}>
          Optimization Weights
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: colors.secondary, mb: 2 }}>
          Adjust the importance of each factor. Weights must sum to 100%.
        </Typography>

        {[
          { key: 'cost' as const, label: 'Cost Optimization', color: colors.chart.primary },
          { key: 'leadTime' as const, label: 'Lead Time', color: colors.chart.purple },
          { key: 'risk' as const, label: 'Risk Reduction', color: colors.chart.cyan },
          { key: 'diversification' as const, label: 'Supplier Diversification', color: colors.chart.warning },
        ].map(({ key, label, color }) => (
          <Box key={key} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{label}</Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color }}>{weights[key]}%</Typography>
            </Box>
            <Slider
              value={weights[key]}
              onChange={(_, v) => handleWeightChange(key, v as number)}
              min={0}
              max={100}
              step={5}
              sx={{
                color,
                '& .MuiSlider-thumb': { width: 16, height: 16 },
              }}
            />
          </Box>
        ))}

        {totalWeight !== 100 && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem' }}>
            Weights sum to {totalWeight}%. Must equal 100%.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1.5 }}>
          Constraints (Optional)
        </Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Max Cost ($)"
            type="number"
            size="small"
            value={maxCost}
            onChange={(e) => setMaxCost(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Max Lead Time (days)"
            type="number"
            size="small"
            value={maxLeadTime}
            onChange={(e) => setMaxLeadTime(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Min Suppliers"
            type="number"
            size="small"
            value={minSupplierCount}
            onChange={(e) => setMinSupplierCount(e.target.value)}
            sx={{ flex: 1 }}
          />
        </Box>

        {generateMutation.isError && (
          <Alert severity="error" sx={{ mt: 2, fontSize: '0.8rem' }}>
            Failed to generate scenario. Please try again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: colors.secondary }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleGenerate}
          disabled={!isValid || generateMutation.isPending}
          startIcon={generateMutation.isPending ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ textTransform: 'none' }}
        >
          {generateMutation.isPending ? 'Generating...' : 'Generate Scenario'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomScenarioBuilder;
